import { useEffect } from 'react';
import { Navigate, useRouteError } from 'react-router-dom';

import { ErrorPage } from '@/app/error-boundary/error-page';
import { sentryCaptureRenderError } from '@/utils/sentry/sentry-render-error';

import {
  attemptChunkReloadForError,
  canAttemptChunkReload,
  isProbablyChunkLoadError,
  reportChunkLoadError,
} from './chunk-load-recovery';

type Props = {
  fallbackTo: string;
};

export function RouteErrorFallback({ fallbackTo }: Props) {
  const error = useRouteError();
  const isChunkError = isProbablyChunkLoadError(error);
  const willReload = isChunkError && canAttemptChunkReload();

  useEffect(() => {
    if (!isChunkError) {
      sentryCaptureRenderError({ error, source: 'router.errorElement' });
      return;
    }

    const didReload = attemptChunkReloadForError(error, { source: 'router.errorElement' });
    if (!didReload) {
      reportChunkLoadError(error, { source: 'router.errorElement' }, { action: 'redirect' });
    }
  }, [error, isChunkError]);

  if (willReload) return null;
  if (isChunkError) return <Navigate to={fallbackTo} replace />;

  return <ErrorPage onRetry={() => window.location.reload()} />;
}
